import { PlayerInput, PlayerState, UpgradeTree } from '@push.io/shared';
import type { PublicPlayerAppearance } from '@push.io/shared';
import { GameState } from '../game/game-state.js';

const DEFAULT_APPEARANCE: PublicPlayerAppearance = {
  baseColorId: 'base_color_blue', skinId: null, patternId: null, flagId: null, letterId: null,
  accessoryId: null, trailId: null, nameplateId: null, badgeId: null, victoryEffectId: null,
};

export type TutorialHighlight = 'player' | 'orb' | 'upgrades' | 'upgrade-push'
  | 'upgrade-weight' | 'upgrade-speed' | 'upgrade-control' | 'bot' | 'arena' | 'rivals';
export interface TutorialStep {
  id: string;
  text: { es: string; en: string; fr: string };
  objective: string;
  highlight: TutorialHighlight;
  allowMovement: boolean;
}

export const TUTORIAL_STEPS: TutorialStep[] = [
  { id: 'move', text: { es: 'Muévete para empezar.', en: 'Move to begin.', fr: 'Déplacez-vous pour commencer.' }, objective: 'move', highlight: 'player', allowMovement: true },
  { id: 'orb', text: { es: 'Recoge orbes para mejorar.', en: 'Collect orbs to upgrade.', fr: 'Ramassez des orbes pour progresser.' }, objective: 'collect', highlight: 'orb', allowMovement: true },
  { id: 'explain-push', text: { es: 'Empuje aumenta la fuerza de tus impactos.', en: 'Push increases the force of your impacts.', fr: "La Poussée augmente la force de vos impacts." }, objective: 'learn', highlight: 'upgrade-push', allowMovement: false },
  { id: 'explain-weight', text: { es: 'Peso reduce cuánto te hacen retroceder.', en: 'Weight reduces how far you are knocked back.', fr: 'Le Poids réduit la distance de votre recul.' }, objective: 'learn', highlight: 'upgrade-weight', allowMovement: false },
  { id: 'explain-speed', text: { es: 'Velocidad aumenta tu rapidez máxima.', en: 'Speed increases your maximum movement speed.', fr: 'La Vitesse augmente votre vitesse maximale.' }, objective: 'learn', highlight: 'upgrade-speed', allowMovement: false },
  { id: 'explain-control', text: { es: 'Control mejora el giro y la recuperación.', en: 'Control improves turning and recovery.', fr: 'Le Contrôle améliore les virages et la récupération.' }, objective: 'learn', highlight: 'upgrade-control', allowMovement: false },
  { id: 'upgrade', text: { es: 'Ahora elige una mejora.', en: 'Now choose an upgrade.', fr: 'Choisissez maintenant une amélioration.' }, objective: 'upgrade', highlight: 'upgrades', allowMovement: false },
  { id: 'impact', text: { es: 'Empuja a tus rivales chocando contra ellos.', en: 'Push rivals by crashing into them.', fr: 'Poussez vos rivaux en les percutant.' }, objective: 'impact', highlight: 'bot', allowMovement: true },
  { id: 'counterattack', text: { es: 'El rival contraataca.', en: 'The rival strikes back.', fr: "Le rival contre-attaque." }, objective: 'observe', highlight: 'bot', allowMovement: false },
  { id: 'knockback', text: { es: 'Si te golpean, te harán retroceder.', en: 'If you are hit, you will be knocked back.', fr: 'Si vous êtes touché, vous reculerez.' }, objective: 'observe', highlight: 'player', allowMovement: false },
  { id: 'arena', text: { es: 'La arena irá desapareciendo.', en: 'The arena will gradually disappear.', fr: "L'arène va progressivement disparaître." }, objective: 'observe', highlight: 'arena', allowMovement: false },
  { id: 'eliminate', text: { es: 'Elimina rivales empujándolos hacia el límite de la arena.', en: 'Eliminate rivals by pushing them towards the arena edge.', fr: "Éliminez vos rivaux en les poussant vers la limite de l'arène." }, objective: 'eliminate', highlight: 'rivals', allowMovement: true },
  { id: 'perfect', text: { es: '¡Perfecto!', en: 'Perfect!', fr: 'Parfait !' }, objective: 'survive', highlight: 'player', allowMovement: false },
  { id: 'ready', text: { es: 'Ya estás preparado.', en: "You're ready.", fr: 'Vous êtes prêt.' }, objective: 'complete', highlight: 'player', allowMovement: false },
];

type Listener = (step: TutorialStep, player: PlayerState, players: PlayerState[]) => void;
type KnockbackAnimation = { x: number; y: number; elapsed: number; moved: number };

export class TutorialManager {
  private stepIndex = 0;
  private stepElapsed = 0;
  private input = { x: 0, y: 0 };
  private active = false;
  private listener: Listener | null = null;
  private complete: (() => void) | null = null;
  private eliminatedOne = false;
  private stepActionDone = false;
  private approachingBotsSpawned = false;
  private counterattackRecoil = { x: 0, y: 0 };
  private counterattackRecoilMoved = 0;
  private impactKnockback: KnockbackAnimation | null = null;
  private eliminationKnockbacks = new Map<string, KnockbackAnimation>();

  constructor(private state: GameState) {}

  start(listener: Listener, complete: () => void): void {
    this.listener = listener;
    this.complete = complete;
    this.active = true;
    this.stepIndex = 0;
    this.stepElapsed = 0;
    this.stepActionDone = false;
    this.approachingBotsSpawned = false;
    this.counterattackRecoil = { x: 0, y: 0 };
    this.counterattackRecoilMoved = 0;
    this.impactKnockback = null;
    this.eliminationKnockbacks.clear();
    this.setupArena();
    this.notify();
  }

  stop(): void {
    this.active = false;
    this.input = { x: 0, y: 0 };
  }

  isActive(): boolean { return this.active; }
  getStep(): TutorialStep { return TUTORIAL_STEPS[this.stepIndex]; }

  handleInput(input: PlayerInput): void {
    if (!this.active || !this.getStep().allowMovement) {
      this.input = { x: 0, y: 0 };
      return;
    }
    this.input = { x: input.dirX, y: input.dirY };
    if (this.getStep().id === 'move' && Math.hypot(input.dirX, input.dirY) > 0.2) this.advance();
  }

  purchase(stat: UpgradeTree): boolean {
    const player = this.state.getLocalPlayer();
    if (!player || this.getStep().id !== 'upgrade' || player.orbs < 2) return false;
    player.orbs -= 2;
    player.upgrades[stat] = Math.min(4, player.upgrades[stat] + 1);
    this.advance();
    this.notify();
    return true;
  }

  continueExplanation(): boolean {
    if (!this.active || this.getStep().objective !== 'learn') return false;
    this.advance();
    return true;
  }

  update(deltaMs: number): void {
    if (!this.active) return;
    this.stepElapsed += deltaMs;
    const player = this.state.getLocalPlayer();
    if (!player) return;

    const speed = 0.0065 * (1 + player.upgrades.speed * 0.12);
    player.x += this.input.x * speed * deltaMs;
    player.y += this.input.y * speed * deltaMs;
    player.angle = Math.atan2(this.input.y, this.input.x);
    const max = this.state.arena.radius - player.radius;
    const distance = Math.hypot(player.x, player.y);
    if (distance > max) { player.x *= max / distance; player.y *= max / distance; }

    const stepId = this.getStep().id;
    if (stepId === 'orb') this.collectOrb(player);
    if (stepId === 'impact') this.hitPracticeBot(player, deltaMs);
    if (stepId === 'counterattack') this.runCounterattack(player, deltaMs);
    if (stepId === 'knockback') this.runKnockbackDemo(player, deltaMs);
    if (stepId === 'arena') this.runArenaDemo();
    if (stepId === 'eliminate') this.runElimination(player, deltaMs);
    if (stepId === 'perfect' && this.stepElapsed > 850) this.advance();
    if (stepId === 'ready' && this.stepElapsed > 1600) this.finish();
    this.notify();
  }

  private setupArena(): void {
    const player = makePlayer('tutorial-player', 'Jugador', 0, 0);
    this.state.players.clear();
    this.state.players.set(player.id, player);
    this.state.setPlayerId(player.id);
    this.state.localPlayer = player;
    this.state.phase = 'playing';
    this.state.arena.radius = 12;
    this.state.arena.initialRadius = 12;
    this.state.arena.brokenSegments = [];
    this.state.orbs = [{ id: 1, x: 5.5, y: 0, radius: .34, collected: false, spawnTick: 0, value: 3 }];
  }

  private collectOrb(player: PlayerState): void {
    const orb = this.state.orbs[0];
    if (orb && Math.hypot(player.x - orb.x, player.y - orb.y) < 0.9) {
      player.orbs += 3;
      this.state.orbs = [];
      this.advance();
    }
  }

  private hitPracticeBot(player: PlayerState, deltaMs: number): void {
    let bot = this.state.players.get('practice-bot');
    if (!bot) {
      bot = makePlayer('practice-bot', 'Bot', player.x + 2.4, player.y);
      this.state.players.set(bot.id, bot);
    }

    if (this.impactKnockback) {
      if (this.animateKnockback(bot, this.impactKnockback, deltaMs, 3.5, 650)) {
        this.impactKnockback = null;
        this.advance();
      }
      return;
    }

    const dx = bot.x - player.x;
    const dy = bot.y - player.y;
    const distance = Math.hypot(dx, dy);
    if (distance < 1.15) {
      this.impactKnockback = this.createKnockback(
        dx, dy, Math.cos(player.angle), Math.sin(player.angle),
      );
      this.input = { x: 0, y: 0 };
    }
  }

  private createKnockback(dx: number, dy: number, fallbackX: number, fallbackY: number): KnockbackAnimation {
    const distance = Math.hypot(dx, dy);
    return {
      x: distance > 0.001 ? dx / distance : fallbackX,
      y: distance > 0.001 ? dy / distance : fallbackY,
      elapsed: 0,
      moved: 0,
    };
  }

  private animateKnockback(
    target: PlayerState,
    animation: KnockbackAnimation,
    deltaMs: number,
    distance: number,
    duration: number,
  ): boolean {
    animation.elapsed = Math.min(duration, animation.elapsed + deltaMs);
    const progress = animation.elapsed / duration;
    const easedDistance = distance * (1 - Math.pow(1 - progress, 3));
    const movement = easedDistance - animation.moved;
    target.x += animation.x * movement;
    target.y += animation.y * movement;
    animation.moved = easedDistance;
    return progress >= 1;
  }

  private runKnockbackDemo(player: PlayerState, deltaMs: number): void {
    if (this.stepElapsed > 2200 && !this.approachingBotsSpawned) {
      this.spawnApproachingBots(player);
      this.approachingBotsSpawned = true;
    }
    if (this.approachingBotsSpawned) this.moveApproachingBots(player, deltaMs);
    if (this.stepElapsed > 4900) {
      this.stepIndex = TUTORIAL_STEPS.findIndex((step) => step.id === 'eliminate');
      this.stepElapsed = 0;
      this.stepActionDone = false;
      this.approachingBotsSpawned = false;
      this.notify();
    }
  }

  private runCounterattack(player: PlayerState, deltaMs: number): void {
    const bot = this.state.players.get('practice-bot');
    if (!bot) {
      this.advance();
      return;
    }
    const dx = player.x - bot.x;
    const dy = player.y - bot.y;
    const distance = Math.max(0.001, Math.hypot(dx, dy));
    const contactDistance = 1.22;
    if (!this.stepActionDone && distance > contactDistance && this.stepElapsed <= 2500) {
      const remaining = distance - contactDistance;
      const movement = Math.min(remaining, deltaMs * 0.0052);
      bot.x += dx / distance * movement;
      bot.y += dy / distance * movement;
      bot.angle = Math.atan2(dy, dx);
      if (remaining - movement <= 0.01) this.beginCounterattackRecoil(player, bot);
    } else if (!this.stepActionDone) {
      this.beginCounterattackRecoil(player, bot);
    } else if (this.stepElapsed < 550) {
      const progress = Math.min(1, this.stepElapsed / 550);
      const easedDistance = 1.8 * (1 - Math.pow(1 - progress, 3));
      const movement = easedDistance - this.counterattackRecoilMoved;
      player.x += this.counterattackRecoil.x * movement;
      player.y += this.counterattackRecoil.y * movement;
      this.counterattackRecoilMoved = easedDistance;
    } else if (this.stepElapsed > 900) {
      this.advance();
    }
  }

  private beginCounterattackRecoil(player: PlayerState, bot: PlayerState): void {
    const dx = player.x - bot.x;
    const dy = player.y - bot.y;
    const distance = Math.max(0.001, Math.hypot(dx, dy));
    this.counterattackRecoil = { x: dx / distance, y: dy / distance };
    this.counterattackRecoilMoved = 0;
    this.stepActionDone = true;
    this.stepElapsed = 0;
  }

  private spawnApproachingBots(player: PlayerState): void {
    void player;
    const practiceBot = this.state.players.get('practice-bot');
    if (practiceBot) practiceBot.name = 'Bot 1';

    [1, 2].forEach((index) => {
      const angle = index * Math.PI * 2 / 3;
      const edgeDistance = this.state.arena.radius - 1;
      const bot = makePlayer(`easy-bot-${index}`, `Bot ${index + 1}`,
        Math.cos(angle) * edgeDistance, Math.sin(angle) * edgeDistance);
      this.state.players.set(bot.id, bot);
    });
  }

  private moveApproachingBots(player: PlayerState, deltaMs: number): void {
    const approach = Math.min(1, deltaMs * 0.00125);
    [1, 2].forEach((index) => {
      const bot = this.state.players.get(`easy-bot-${index}`);
      if (!bot) return;
      const angle = index * Math.PI * 2 / 3;
      const targetX = player.x + Math.cos(angle) * 2.6;
      const targetY = player.y + Math.sin(angle) * 2.6;
      bot.x += (targetX - bot.x) * approach;
      bot.y += (targetY - bot.y) * approach;
    });
  }

  private runArenaDemo(): void {
    this.state.arena.radius = Math.max(10.2, 12 - this.stepElapsed / 1200);
    this.state.arena.brokenSegments = [{ startAngle: -.4, endAngle: .35, brokenAt: this.state.tick }];
    if (this.stepElapsed > 2300) {
      this.spawnEasyBots();
      this.advance();
    }
  }

  private spawnEasyBots(): void {
    this.state.players.delete('practice-bot');
    const player = this.state.getLocalPlayer()!;
    [0, 1, 2].forEach((index) => {
      const angle = index * Math.PI * 2 / 3;
      const bot = makePlayer(`easy-bot-${index}`, `Bot ${index + 1}`,
        player.x + Math.cos(angle) * 2.2, player.y + Math.sin(angle) * 2.2);
      this.state.players.set(bot.id, bot);
    });
  }

  private runElimination(player: PlayerState, deltaMs: number): void {
    for (const bot of this.state.players.values()) {
      if (bot.id === player.id || !bot.alive) continue;
      const activeKnockback = this.eliminationKnockbacks.get(bot.id);
      if (activeKnockback) {
        const finished = this.animateKnockback(bot, activeKnockback, deltaMs, 1.5, 320);
        if (Math.hypot(bot.x, bot.y) >= this.state.arena.radius - bot.radius) {
          bot.alive = false;
          bot.eliminated = true;
          this.eliminatedOne = true;
          this.eliminationKnockbacks.delete(bot.id);
          this.advance();
          break;
        }
        if (finished) this.eliminationKnockbacks.delete(bot.id);
        continue;
      }

      const dx = bot.x - player.x;
      const dy = bot.y - player.y;
      if (Math.hypot(dx, dy) < 1.15) {
        this.eliminationKnockbacks.set(
          bot.id,
          this.createKnockback(dx, dy, Math.cos(player.angle), Math.sin(player.angle)),
        );
      }
    }
  }

  private advance(): void {
    if (!this.active || this.stepIndex >= TUTORIAL_STEPS.length - 1) return;
    this.stepIndex++;
    this.stepElapsed = 0;
    this.stepActionDone = false;
    this.approachingBotsSpawned = false;
    this.counterattackRecoil = { x: 0, y: 0 };
    this.counterattackRecoilMoved = 0;
    this.impactKnockback = null;
    this.eliminationKnockbacks.clear();
    this.input = { x: 0, y: 0 };
    this.notify();
  }

  private finish(): void {
    if (!this.active) return;
    this.active = false;
    this.complete?.();
  }

  private notify(): void {
    const player = this.state.getLocalPlayer();
    if (player) this.listener?.(this.getStep(), { ...player, upgrades: { ...player.upgrades } },
      Array.from(this.state.players.values()).map((item) => ({ ...item, upgrades: { ...item.upgrades } })));
  }
}

function makePlayer(id: string, name: string, x: number, y: number): PlayerState {
  return {
    id, name, x, y, vx: 0, vy: 0, angle: 0, radius: .5, alive: true, orbs: 0,
    upgrades: { push: 0, weight: 0, speed: 0, control: 0 },
    desiredDirX: 0, desiredDirY: 0, knockbackTicks: 0, impactImmunityUntilTick: 0,
    recoveryTimer: 0, sequence: 0, appearance: DEFAULT_APPEARANCE,
    eliminated: false, placement: 0,
  };
}
